<template>
  <nav class="pagination__wrapper">
    <template v-if="!disableItemsPerPage">
      <div class="pagination__per-page">
        <span class="per_page">{{ $t('pagination.rows_per_page') }}</span>
        <FormControl
            v-model="perPage"
            type="select"
            label-disabled
            disable-search
            :payload="perPageList"
            input-group-class="pages-select"
            :clearable="false"
        />
        <span class="page_of">{{ $t('pagination.rows_per_page_of') }}</span>
        <span class="total">{{ total }}</span>
      </div>
    </template>

    <ul class="pagination">
      <li class="pagination__arrow">
        <a class="page-link"
           :class="{'disabled': prevIsDisabled}"
           @click="prevPage"
        >
          <i class="icon-chevron-left action-icon" />
        </a>
      </li>

      <li v-for="page in pages"
          :key="page"
          class="page-item"
          @click="onClick(page)"
      >
        <a class="page-link"
           :class="{
              'active': page == currentPage
           }"
        >
          {{ page }}
        </a>
      </li>

      <li class="pagination__arrow">
        <a class="page-link"
           :class="{'disabled': nextIsDisabled}"
           @click="nextPage"
        >
          <i class="icon-chevron-right action-icon" />
        </a>
      </li>
    </ul>

    <template v-if="showGoToPage">
      <div class="pagination__go-to-page">
        <span>{{ $t('pagination.go_to_page')}}</span>
        <FormControl
            v-model="currentPage"
            small
            label-disabled
            input-group-class="go_to_page"
            :clearable="false"
            @change="change"
        />
      </div>
    </template>
  </nav>
</template>
<script setup>
import {computed, reactive, ref, watch} from "vue";
import FormControl from "@/js/Elements/Form/FormControl.vue";

const emit = defineEmits(['update:modelValue', 'change', 'update:perPage']);
const props = defineProps({
  modelValue: {
    type: [String, Number],
    default: 1,
  },
  total: Number,
  totalPages: {
    type: [String, Number],
    default: 1,
  },
  pagesCount: {
    type: [String, Number],
    default: 7,
  },
  perPage: [Number, String],
  perPageList: Array,
  disabled: Boolean,
  disableItemsPerPage: Boolean,
  disableGoToPage: Boolean,
});

const currentPage = ref(props.modelValue);
const pages = reactive([]);
const nextIsDisabled = computed(() => currentPage.value === props.totalPages);
const prevIsDisabled = computed(() => currentPage.value < 2);
const perPage = ref(props.perPage);
const showGoToPage = computed(() => {
  return !props.disableGoToPage && props.totalPages > 1;
})

watch(() => props.perPage, (value) => perPage.value = value);
watch(() => perPage.value, (value) => emit('update:perPage', value));

generatePages();

watch(
    () => props.modelValue,
    change
);

watch(
    () => props.totalPages,
    generatePages
);

function generatePages() {
  pages.splice(0);
  const offset = props.pagesCount % 2 ? Math.floor(props.pagesCount / 2) : 1;
  let start = currentPage.value - offset;
  start = start < 1 ? 1 : start;

  let end = start + (props.pagesCount - 1);
  end = end > props.totalPages ? props.totalPages : end;

  const diff = end - start;
  if (diff < props.pagesCount && props.totalPages >= props.pagesCount) {
    start = end - (props.pagesCount - 1);
  }

  for(let page = start; page <= end; page++) {
    if (page > props.totalPages) break;
    pages.push(page);
  }
}

function prevPage() {
  if (props.disabled) return;
  change(currentPage.value - 1);
}

function nextPage() {
  if (props.disabled) return;
  change(currentPage.value + 1);
}


function onClick(page) {
  if (props.disabled || currentPage.value === page) return;
  change(page);
}

function change(page) {
  if (isNaN(page)) return;
  if (currentPage.value === page) return;

  page = page > props.totalPages ? props.totalPages : page;
  page = page < 1 ? 1 : page;
  currentPage.value = page;
  generatePages();
  emit('update:modelValue', page);
  emit('change', page);
}
</script>
<style lang="scss" scoped>
@import "@/css/functions";
@import "@/css/variables";
@import "bootstrap/scss/functions";
@import "bootstrap/scss/mixins";
@import "bootstrap/scss/variables";

.pagination__wrapper {
  display: flex;
  align-items: center;
  gap: spacing(2);

  .pagination__per-page,
  .pagination__go-to-page {
    display: flex;
    align-items: center;
    gap: spacing(2);

    > span {
      white-space: nowrap;
    }
  }

  .pagination__go-to-page {
    &:deep(.go_to_page) {
      max-width: toRem(31);
      height: toRem(26);

      input {
        text-align: center;
        padding: 0 spacing(1);
      }
    }
  }

  .pagination {
    gap: spacing(2);
    margin-bottom: 0;

    .page-link,
    &__arrow {
      display: flex;
      align-items: center;
      justify-content: center;
      width: toRem(24);
      aspect-ratio: 1/1;
    }

    &__arrow {
      padding-bottom: toRem(3);
    }

    a {
      cursor: pointer;
    }

    &__arrow > a {
      border: none;
    }

    .page-link {
      border-radius: $border-radius-sm;
      color: themeColor('text');

      &.active {
        font-weight: $font-weight-medium;
        border-color: themeColor('border');
      }
    }
  }
}


.pagination__wrapper {
  width: 100%;
  flex-wrap: wrap;
  font-size: fontSize(5);
}

.pagination {
  order: 1;
  display: flex;
  justify-content: center;
  width: 100%;
  padding-bottom: spacing(2);
  border-bottom: themeColor('border') solid toRem(1);
}

.pagination__per-page {
  order: 2;
  margin-left: spacing(2);

  .per_page {
    max-width: toRem(50);
    overflow: hidden;
    text-overflow: ellipsis;
  }

  &:deep(.pages-select) {
    width: auto;
    height: toRem(26);

    .select__output {
      padding-top: spacing(1);
      padding-right: 0;
      padding-left: spacing(2);
    }

    .select__arrow {
      padding-top: toRem(3);
      padding-right: spacing(2);
      padding-left: spacing(2);
    }
  }
}

.pagination__go-to-page {
  order: 3;
  margin-left: auto;
  margin-right: spacing(2);
}

@include media-breakpoint-up(sm) {
  .pagination__wrapper {
    flex-wrap: nowrap;
    justify-content: flex-end;
  }

  .pagination {
    width: auto;
    order: 2;
    border-bottom: none;
    padding-bottom: 0;
  }

  .pagination__go-to-page {
    margin-left: unset;
    margin-right: unset;
  }

  .pagination__per-page {
    order: 1;
    .per_page {
      max-width: unset;
    }
  }
}
</style>